import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useNavigate } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import { UserHttpService } from 'core/services/user';
import {
    Alert,
    Box,
    Button,
    Divider,
    TextField,
    Typography,
    IconButton,
    InputAdornment
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import { ThemeProvider } from '@mui/material/styles';
import publicTheme from 'core/theme/publicTheme';

function LoginBox({ onClose, redirectUrl }) {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const { loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const getResponseError = (response) => {
        if (!response) return 'Unable to sign in. Please try again.';
        if (response.detail) return response.detail;
        if (response.email) return response.email;
        if (Array.isArray(response) && response[0]) return response[0];
        return 'Invalid credentials. Please try again.';
    };

    const onSubmit = async (data) => {
        if (loading) return;

        setLoading(true);
        setErrorMessage("");
        const body = {
            "email": data.email,
            "password": data.password
        };

        try {
            const response = await UserHttpService.doLogin(body);
            if (response?.tokens?.access) {
                HttpService.setToken(response.tokens.access);
                HttpService.setUser({
                    email: response.email,
                    role: response.role,
                    first_name: response.first_name,
                    last_name: response.last_name
                });

                // Close modal if it exists
                if (onClose) onClose();
                
                // Handle redirect URL if provided
                if (redirectUrl && redirectUrl !== '/') {
                    window.location.href = redirectUrl;
                    return;
                }
                
                // Redirect based on user role
                if (response.role === 'employer') {
                    window.location.href = '/employer/dashboard';
                } else if (response.role === 'admin') {
                    window.location.href = '/cms/dashboard';
                } else if (response.role === 'employee') {
                    window.location.href = '/jobseeker/dashboard';
                } else {
                    // If no redirect URL and no specific role, navigate to home or reload
                    if (onClose) {
                        // If modal, just reload to refresh the page
                        window.location.reload();
                    } else {
                        // If login page, navigate to home
                        navigate('/');
                    }
                }
                return;
            }
            setErrorMessage(getResponseError(response));
        } catch (error) {
            console.error('Login error:', error);
            // Extract error message from error object
            let errorMsg = 'Unable to sign in. Please check your connection and try again.';
            if (error.message) {
                errorMsg = error.message;
            } else if (error.response?.data) {
                const errorData = error.response.data;
                errorMsg = errorData.detail || errorData.error || errorMsg;
            } else if (error.detail) {
                errorMsg = error.detail;
            }
            setErrorMessage(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleOAuthLogin = (provider = 'google') => {
        // Auth0 handles multiple providers, but we'll use loginWithRedirect
        // which can be configured to show provider selection
        loginWithRedirect({
            authorizationParams: {
                connection: provider === 'google' ? 'google-oauth2' : undefined
            }
        });
    };

    const handleSignupClick = (e) => {
        e.preventDefault();
        if (onClose) onClose();
        navigate('/signup');
    };

    return (
        <ThemeProvider theme={publicTheme}>
        <Box sx={{ p: onClose ? 3 : 0, width: '100%', maxWidth: 450, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                <Box component="img" alt="Job Flow Connect" src="/assets/images/jlogo.png" sx={{ height: 34, mb: 2.5 }} />
                <Typography variant="h5" fontWeight={800} gutterBottom>
                    Welcome back
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Sign in to your account to continue
                </Typography>
            </Box>

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <TextField
                    fullWidth
                    label="Email address"
                    type="email"
                    margin="normal"
                    autoComplete="email"
                    {...register("email", { 
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    })}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailOutlinedIcon color="action" fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    autoComplete="current-password"
                    {...register("password", { required: "Password is required" })}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockOutlinedIcon color="action" fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    edge="end"
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ mt: 2, mb: 2, py: 1.4 }}
                >
                    {loading ? 'Signing in…' : 'Sign In'}
                </Button>
            </Box>

            <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    OR
                </Typography>
            </Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<GoogleIcon />}
                    onClick={() => handleOAuthLogin('google')}
                    sx={{ 
                        textTransform: 'none',
                        borderColor: 'divider',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    Continue with Google
                </Button>

                <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={() => loginWithRedirect()}
                    sx={{ 
                        textTransform: 'none',
                        borderColor: 'divider',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover'
                        }
                    }}
                >
                    Continue with Auth0
                </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link 
                        to="/signup" 
                        onClick={handleSignupClick}
                        style={{ 
                            color: '#4a5bb0', 
                            textDecoration: 'none',
                            fontWeight: 600
                        }}
                    >
                        Sign up
                    </Link>
                </Typography>
            </Box>
        </Box>
        </ThemeProvider>
    );
}

export default LoginBox;
